import _ from 'lodash'
import React, { useContext, useState } from 'react'
import { Grid, Label, Segment } from 'semantic-ui-react'
import { uniqueKey } from '../../../../global/common/keys'
import { gEnums } from '../../../../global/enums/globalEnums'
import { SeasonMatchesContext } from '../../cnr/contexts/SeasonMatchesContext'
import { _matchDisplayTypes, MatchContentWithProvider } from './MatchContent'
import { PlayoffMatchesContext } from 'projectSpecific/sports/cnr/contexts/PlayoffMatchesProvider'

export const playoffBracketTypesByCount = (count) => {

  switch (count) {
    case 16:
      return playoffBracketTypes.roundOf32
    case 8:
      return playoffBracketTypes.roundOf16
    case 4:
      return playoffBracketTypes.quarterFinals
    case 2:
      return playoffBracketTypes.semiFinals
    case 1:
      return playoffBracketTypes.finals
    default:
      return playoffBracketTypes.roundOf16
  }
}

const _seeds = {
  r32: {
    left: [1, 16, 9, 8, 5, 12, 13, 4],
    right: [2, 15, 10, 7, 6, 11, 14, 3]
  },
  r16: {
    left: [1, 8, 5, 4],
    right: [2, 7, 6, 3]
  },
  qf: {
    left: [1, 4],
    right: [2, 3],
  },
  sf: {
    left: [1],
    right: [2]
  },
  f: [1, 3]
}

export const playoffBracketTops = {
  finals: [],
  quarterFinals: [1, 4],
  roundOf16: [1, 3, 6, 8],
  roundOf32: [1, 3, 5, 7, 10, 12, 14, 16],
  semiFinals: [],
}

export const playoffBracketTypes = {
  consolation: 'consolation',
  finals: 'finals',
  preliminaryRound: 'preliminaryRound',
  quarterFinals: 'quarterFinals',
  roundOf16: 'roundOf16',
  roundOf32: 'roundOf32',
  semiFinals: 'semiFinals',
}

const bracketTypes = {
  startFull: 'startFull',
  startSolo: 'startSolo',
  solo: 'solo'
}

const useGrid = true

const PlayoffBracket = (props) => {

  const { pool, tournamentType: tournamentType_prop } = props ?? {}

  // seasonMatchesContext
  const playoffMatchesContext = useContext(PlayoffMatchesContext)
  const { playoffMatches_state } = playoffMatchesContext ?? {}
  const { pools } = playoffMatches_state ?? {}

  const [selectedBracket] = useState(bracketTypes.startSolo)

  const _bracketSeedSums = {}

  _.forEach(playoffBracketTypes, (b) => {
    const _pool = pools ? pools[b] : {}
    const { matches } = _pool ?? {}
    const _matches = _.filter(matches, match => match.isConsolation === false || match.isConsolation === undefined);
    const teamCount = _.size(_matches) * 2

    const totalSeeds = _.sumBy(_.values(_matches), match => {
      const homeSeed = _.get(match.home, 'seed', 0);
      // If no away seed, calculate based on the home seed
      const awaySeed = _.get(match.away, 'seed', (teamCount + 1) - homeSeed);
      return homeSeed + awaySeed;
    });
    const totalSum = (teamCount * (teamCount + 1)) / 2;
    _bracketSeedSums[b] = totalSeeds - totalSum
  })

  const getMatch = (match, keyOrIndex) => {

    const { key, id, poolKey, matchKey, _results_matchDay } = match

    // if (_results_matchDay) {
    //   match._results = _results_matchDay
    // }

    let keyy = key ? key : id
    if (!keyy) { keyy = keyOrIndex }

    let matchCss = matchKey

    if (playoffBracketTops[poolKey].length > 0) {
      if (playoffBracketTops[poolKey].includes(match.matchNumber)) {
        matchCss += ' playoff-top'
      } else {
        matchCss += ' playoff-bottom'
      }
    }

    // this is the match!!!
    const m = <MatchContentWithProvider
      matchKey={match._itemKey}
      match={match}
      matchDisplayType={_matchDisplayTypes.playoff_grid_dash}
      matchScheduleDisplayType={gEnums.matchScheduleDisplayTypes.levelTabsFull}
    />

    return useGrid ? <div
      key={uniqueKey('mwd', keyy)}
      className={matchCss}>{m}
    </div> :
      <div key={uniqueKey('mwd', keyy)}
        className={matchCss}>{m}
      </div>
  }

  /**
   * 
   * @param {string} playoffBracketType 
   * @param {object} seeds 
   * @returns a group of matches base on the pool
   */
  const bracket = (playoffBracketType, seeds, side) => {

    const pool_bracket = pools ? pools[playoffBracketType] : {}
    const { matches } = pool_bracket ?? {}

    // eslint-disable-next-line 
    const bs = seeds ? seeds.map((seed, index) => {
      const m = _.find(matches, function (m) {
        return ((m.home && m.home.seed === seed) || (m.pending && m.pending.home && m.pending.home.originalSeed === seed));
      })
      if (m) {
        return getMatch(m, index)
      } else {
        // console.log('No match found')
      }
    }) : []
    return useGrid ? <Grid.Column key={uniqueKey('pb', playoffBracketType, side)}>{bs}</Grid.Column> : bs
  }

  const headerCol = (playoffBracketType) => <Grid.Column key={uniqueKey('pb', playoffBracketType)}><Segment key={uniqueKey('pb', 'seg', playoffBracketType)}>{_.startCase(playoffBracketType)}</Segment></Grid.Column>

  const colz = {
    col32: () => headerCol(playoffBracketTypes.roundOf32),
    col16: () => headerCol(playoffBracketTypes.roundOf16),
    colQf: () => headerCol(playoffBracketTypes.quarterFinals),
    colSf: () => headerCol(playoffBracketTypes.semiFinals),
    colF: () => headerCol(playoffBracketTypes.finals),
    r32_left: () => bracket(playoffBracketTypes.roundOf32, _seeds.r32.left, 'left'),
    r32_right: () => bracket(playoffBracketTypes.roundOf32, _seeds.r32.right, 'right'),
    r16_left: () => bracket(playoffBracketTypes.roundOf16, _seeds.r16.left, 'left'),
    r16_right: () => bracket(playoffBracketTypes.roundOf16, _seeds.r16.right, 'right'),
    quarter_left: () => bracket(playoffBracketTypes.quarterFinals, _seeds.qf.left, 'left'),
    quarter_right: () => bracket(playoffBracketTypes.quarterFinals, _seeds.qf.right, 'right'),
    semi_left: () => bracket(playoffBracketTypes.semiFinals, _seeds.sf.left, 'left'),
    semi_right: () => bracket(playoffBracketTypes.semiFinals, _seeds.sf.right, 'right'),
    finals: () => bracket(playoffBracketTypes.finals, _seeds.f, 'left')
  }

  const headerCols_full = () => {
    const cols = []
    switch (pool.poolKey) {
      case playoffBracketTypes.roundOf32:
        cols.push(colz.col32())
        cols.push(colz.col32())
        break;

      case playoffBracketTypes.roundOf16:
        cols.push(colz.col16())
        cols.push(colz.col16())
        break;

      case playoffBracketTypes.quarterFinals:
        cols.push(colz.col16())
        cols.push(colz.colQf())
        cols.push(colz.colQf())
        cols.push(colz.col16())
        break;

      case playoffBracketTypes.semiFinals:
        cols.push(colz.col16())
        cols.push(colz.colQf())
        cols.push(colz.colSf())
        cols.push(colz.colSf())
        cols.push(colz.colQf())
        cols.push(colz.col16())
        break;

      case playoffBracketTypes.finals:
        cols.push(colz.col16())
        cols.push(colz.colQf())
        cols.push(colz.colSf())
        cols.push(colz.colF())
        cols.push(colz.colSf())
        cols.push(colz.colQf())
        cols.push(colz.col16())
        break;

      default:
        break;
    }
    return cols
  }

  const startBracket_full = () => {

    const cols = []
    let columns = 2

    switch (pool.poolKey) {
      case playoffBracketTypes.roundOf32:
        cols.push(colz.r32_left())
        cols.push(colz.r32_right())
        break;

      case playoffBracketTypes.roundOf16:
        cols.push(colz.r16_left())
        cols.push(colz.r16_right())
        break;

      case playoffBracketTypes.quarterFinals:
        cols.push(colz.r16_left())
        cols.push(colz.quarter_left())
        cols.push(colz.quarter_right())
        cols.push(colz.r16_right())
        columns = 4
        break;

      case playoffBracketTypes.semiFinals:
        cols.push(colz.r16_left())
        cols.push(colz.quarter_left())
        cols.push(colz.semi_left())
        cols.push(colz.semi_right())
        cols.push(colz.quarter_right())
        cols.push(colz.r16_right())
        columns = 6
        break;

      case playoffBracketTypes.finals:
        cols.push(colz.r16_left())
        cols.push(colz.quarter_left())
        cols.push(colz.semi_left())
        cols.push(colz.finals())
        cols.push(colz.semi_right())
        cols.push(colz.quarter_right())
        cols.push(colz.r16_right())
        columns = 7
        break;

      default:
        break;
    }


    return <div className={'grid-overflow'}>
      <Grid columns={columns} className={'playoff-grid-header'} >
        {headerCols_full()}
      </Grid>
      <Grid columns={columns} className={'playoff-grid'}>
        <Grid.Row>
          {cols}
        </Grid.Row>
      </Grid>
    </div>
  }

  const bracket_grid = (columns, cols) => <div className={'grid-overflow-c'}>
    <div className={'grid-overflow'}>
      <Grid columns={columns} divided className={'playoff-grid'}>
        <Grid.Row>
          {cols}
        </Grid.Row>
      </Grid>
    </div>
  </div>

  const bracket_div = (cols, pk) => <div className={'p-bracket ' + pk}>{cols}</div>

  const startBracket_solo = () => {

    const cols = []
    let columns = 2

    if (pool) {
      switch (pool.poolKey) {
        case playoffBracketTypes.roundOf32:
          cols.push(colz.r32_left())
          cols.push(colz.r32_right())
          break;

        case playoffBracketTypes.roundOf16:
          cols.push(colz.r16_left())
          cols.push(colz.r16_right())
          break;

        case playoffBracketTypes.quarterFinals:
          cols.push(colz.quarter_left())
          cols.push(colz.quarter_right())
          // columns = 1
          break;

        case playoffBracketTypes.semiFinals:
          cols.push(colz.semi_left())
          cols.push(colz.semi_right())
          columns = 1
          break;

        case playoffBracketTypes.finals:
          cols.push(colz.finals())
          columns = 1
          break;

        default:
          break;
      }

    }

    return cols.length > 0 ? useGrid ? bracket_grid(columns, cols) : bracket_div(cols, pool.poolKey) :
      <div>---</div>
  }

  const content = () => {
    switch (selectedBracket) {
      case bracketTypes.startSolo:
        return <React.Fragment>
          {!tournamentType_prop && <div className='pbsd'>
            <Label size='tiny' color='black' circular>{'Seed Difference: '}{_bracketSeedSums[pool.poolKey]}</Label>
          </div>}
          {pools && startBracket_solo()}
        </React.Fragment>
      case bracketTypes.startFull:
        return startBracket_full();
      default:
        break;
    }
  }

  return content()

}

export default PlayoffBracket